import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Spin } from 'antd';
import { LockOutlined, SafetyOutlined, UserOutlined } from '@ant-design/icons';
import { getCaptcha, login } from '@/api/system';
import { setAuthSession } from '@/utils/auth';
import { toast } from '@/utils/toast';

function normalizeCaptchaImage(value?: string) {
  if (!value) return '';
  return value.startsWith('data:image') ? value : `data:image/png;base64,${value}`;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaKey, setCaptchaKey] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const refreshCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const captcha = await getCaptcha();
      setCaptchaKey(captcha.captchaKey);
      setCaptchaImage(normalizeCaptchaImage(captcha.captchaBase64));
      setCaptchaInput('');
    } catch (error: any) {
      toast.error(error.message || '验证码加载失败');
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  const handleLogin = useCallback(async () => {
    if (!username.trim()) {
      toast.warning('请输入账号');
      return;
    }
    if (!password.trim()) {
      toast.warning('请输入密码');
      return;
    }
    if (!captchaInput.trim()) {
      toast.warning('请输入验证码');
      return;
    }
    if (!captchaKey) {
      toast.warning('验证码未加载，请刷新后重试');
      refreshCaptcha();
      return;
    }

    setLoading(true);
    try {
      const token = await login(username.trim(), password, captchaKey, captchaInput.trim());
      setAuthSession({
        accessToken: token.accessToken,
        refreshToken: token.refreshToken,
        tokenType: token.tokenType,
        user: { username: username.trim(), role: '管理员' },
      });
      toast.success('登录成功，欢迎回来！');
      navigate('/', { replace: true });
    } catch (error: any) {
      toast.error(error.message || '登录失败');
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  }, [username, password, captchaInput, captchaKey, navigate, refreshCaptcha]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleLogin();
      }
    },
    [handleLogin]
  );

  return (
    <div className="min-h-screen flex bg-[#f0f2f5]">
      <div className="hidden lg:flex lg:w-[480px] xl:w-[560px] relative overflow-hidden bg-gradient-to-br from-[#0d1f3c] via-[#122c52] to-[#1a3d6e]">
        <div className="absolute inset-0 ">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20interconnected%20triangles%20hexagons%20and%20diamond%20shapes%20in%20dark%20navy%20blue%20charcoal%20and%20muted%20gold%20tones%20arranged%20in%20a%20sophisticated%20tessellation%20pattern%20with%20subtle%20gradients%20and%20layered%20translucent%20polygons%20creating%20depth%20minimal%20clean%20aesthetic%20dark%20elegant%20corporate%20atmosphere%20seamless%20pattern%20style%20modern%20luxurious%20abstract%20wallpaper%20with%20fine%20line%20geometry%20and%20smooth%20matte%20finish&width=900&height=1300&seq=login-geo-pattern-01&orientation=portrait"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f3c]/70 via-[#122c52]/40 to-[#1a3d6e]/80"></div>

        <div className="relative z-10 flex flex-col justify-center px-12 w-full">
          <div className="mb-8">
            <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-6 mt-20">
              <i className="ri-building-2-line text-3xl text-white"></i>
            </div>
            <h1 className="text-4xl font-bold text-white mb-3 tracking-wide">
              儋州市那大镇商会
            </h1>
            <p className="text-lg text-white/70 font-light tracking-widest">
              数字化平台
            </p>
          </div>
          <div className="space-y-4 mt-8">
            <div className="flex items-center gap-3 text-white/80">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-sm"></i>
              </div>
              <span className="text-sm">安全可靠的数据管理</span>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-group-line text-sm"></i>
              </div>
              <span className="text-sm">高效便捷的会员服务</span>
            </div>
            <div className="flex items-center gap-3 text-white/80">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-bar-chart-box-line text-sm"></i>
              </div>
              <span className="text-sm">智能全面的数据分析</span>
            </div>
          </div>
          <div className="mt-auto pt-12 text-white/40 text-xs">
            &copy; 2025 儋州市那大镇商会 版权所有
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden text-center mb-10">
            <div className="w-14 h-14 rounded-2xl bg-[#122c52] flex items-center justify-center mx-auto mb-4">
              <i className="ri-building-2-line text-2xl text-white"></i>
            </div>
            <h2 className="text-xl font-bold text-gray-800">儋州市那大镇商会数字化平台</h2>
          </div>

          <div className="bg-white rounded-2xl p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">欢迎登录</h2>
              <p className="text-sm text-gray-400">请输入您的账号信息进入系统</p>
            </div>

            <div className="space-y-5" onKeyDown={handleKeyDown}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">账号</label>
                <Input
                  size="large"
                  prefix={<UserOutlined className="text-gray-400" />}
                  placeholder="请输入账号"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  className="rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">密码</label>
                <Input.Password
                  size="large"
                  prefix={<LockOutlined className="text-gray-400" />}
                  placeholder="请输入密码"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">验证码</label>
                <div className="flex gap-3">
                  <Input
                    size="large"
                    prefix={<SafetyOutlined className="text-gray-400" />}
                    placeholder="请输入验证码"
                    value={captchaInput}
                    onChange={(event) => setCaptchaInput(event.target.value)}
                    className="rounded-lg flex-1"
                    maxLength={8}
                  />
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    className="h-10 w-[120px] flex-shrink-0 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer select-none hover:bg-gray-200 transition-colors overflow-hidden border-0 p-0"
                    title="点击刷新验证码"
                  >
                    {captchaLoading ? (
                      <Spin size="small" />
                    ) : captchaImage ? (
                      <img src={captchaImage} alt="验证码" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs text-gray-400">点击刷新</span>
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="primary"
                size="large"
                block
                loading={loading}
                onClick={handleLogin}
                className="!h-11 !rounded-lg !text-base !font-medium !bg-[#122c52] hover:!bg-[#1a3d6e] !border-[#122c52]"
              >
                登录
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
